import { z } from 'zod';
import {
  CHAT_HISTORY_LIMIT,
  CHAT_MAX_LENGTH,
  MAX_PLAYERS,
  MAX_ROUND_ID_LENGTH,
  ROOM_CODE_RE,
  type GameConfig,
} from './config';
import type { GameMode } from './modes';
import type { RoundResults, WordView } from './results';
import { roundResultsSchema } from './results';
import { solutionFilterSchema, type SolutionFilter } from './solution-filter';
import {
  activeEffectSchema,
  powerUpSpawnSchema,
  POWER_UP_KINDS,
  TIMED_EFFECT_KINDS,
  type ActiveEffect,
  type PowerUpKind,
  type PowerUpSpawn,
} from './power-ups';
import {
  profileBlockFeatureSchema,
  profileBlockFeaturesSchema,
  type ProfileBlockFeature,
  type ProfileBlockFeatures,
} from './profile-blocks';
import {
  boardSchema,
  configSchema,
  dailyDateSchema,
  modeSchema,
  textSchema as text,
  wordSchema,
} from './schema-primitives';

const PHASES = ['lobby', 'countdown', 'playing', 'results'] as const;
export type Phase = (typeof PHASES)[number];

/** Where an async-pace participant is in the current round; absent in live rooms. */
const TURN_PROGRESS = ['waiting', 'playing', 'finished'] as const;
export type TurnProgress = (typeof TURN_PROGRESS)[number];

export interface PlayerView {
  id: string;
  name: string;
  avatar: string;
  connected: boolean;
  count: number;
  total: number;
  host: boolean;
  spectator?: boolean;
  turn?: TurnProgress;
  /** Live Versus ready check only: whether this member confirmed; absent outside a check. */
  ready?: boolean;
}

/** A live Versus start awaiting guest confirmations; abandoned at `endAt` unless the host starts anyway. */
export interface ReadyCheckView {
  endAt: number;
}

/** A lobby chat entry; identity comes from the server-owned membership, never the client. */
export interface ChatMessage {
  id: string;
  playerId: string;
  name: string;
  avatar: string;
  text: string;
  at: number;
  /** System lines narrate membership; absent on entries stored before they existed. */
  kind?: 'player' | 'system';
}

/**
 * Endless rounds only: which board of the round is in play and when it is replaced. `endAt` is 0
 * until the player's first touch on this board starts its clock.
 */
export interface EndlessBoard {
  number: number;
  endAt: number;
}

export interface StateMessage {
  t: 'state';
  now: number;
  phase: Phase;
  round: number;
  currentRoundId: string | null;
  config: GameConfig;
  mode: GameMode;
  /** Central calendar date for daily rooms; omitted for non-daily rooms. */
  dailyDate?: string;
  activeConfig: GameConfig | null;
  startAt: number;
  endAt: number;
  board: string[] | null;
  players: PlayerView[];
  /** Recent lobby chat for backfill; live entries arrive as `chat` messages. */
  chat: ChatMessage[];
  results: RoundResults | null;
  resultLifecycle: { continuation: 'continue-room' | 'archive-only'; archiveRoundId: string | null };
  /**
   * Live Versus shared reveal start (server milliseconds). Null outside a gated
   * Live Versus recap or when the round predates reveal timestamps; settled.
   */
  revealAt?: number | null;
  /** Present only while the host's start waits for guests to confirm. */
  readyCheck?: ReadyCheckView;
  joinUrl: string;
  /** Chaos rounds only: the round's full spawn schedule, consumed entries marked with `takenBy`. */
  powerUps?: PowerUpSpawn[];
  /** Endless rounds in play only. */
  endlessBoard?: EndlessBoard;
  you: {
    id: string;
    name: string;
    avatar: string;
    words: WordView[];
    spectator: boolean;
    finished?: boolean;
    /** Async pace only: this participant has started their own timed turn. */
    begun?: boolean;
    /** Chaos rounds only: effects currently shaping this participant's play, and a held shield. */
    effects?: ActiveEffect[];
    shield?: boolean;
    /**
     * While this participant can enter words: a Bloom filter of the round's solutions, so
     * the browser rejects non-words instantly and presents hits as accepted before the
     * server's authoritative verdict. Absent for spectators, finished turns and results.
     */
    solutionFilter?: SolutionFilter;
  };
}

/** A power-up was consumed; `targets` received its effect, `shielded` absorbed it. */
export interface PowerUpMessage {
  t: 'powerUp';
  roundId: string;
  id: string;
  kind: PowerUpKind;
  by: string;
  targets: string[];
  shielded: string[];
  until: number | null;
}

/** Client commands are derived from the parsing schema so the two cannot drift. */
/**
 * WebSocket close codes shared by the room, the Matchmaker and the browser. `sessionReplaced`
 * tells a tab another tab took over its seat, so it stops reconnecting; `abnormal` is what the
 * browser reports for a connection it abandoned itself.
 */
export const CLOSE_CODES = {
  normal: 1000,
  abnormal: 1006,
  policy: 1008,
  sessionReplaced: 4001,
} as const;

export type ClientMessage = z.infer<typeof clientSchema>;

export type ServerMessage =
  | { t: 'pong'; c: number; s: number }
  | { t: 'welcome'; token: string; id: string }
  | StateMessage
  | { t: 'players'; players: PlayerView[] }
  | { t: 'count'; id: string; n: number }
  | { t: 'chat'; message: ChatMessage }
  | { t: 'hint'; roundId: string; w: string | null }
  | { t: 'wordResult'; submissionId?: string; roundId: string; w: string; ok: true; pts: number }
  | {
      t: 'wordResult';
      submissionId?: string;
      roundId: string;
      w: string;
      ok: false;
      reason: WordRejectReason;
    }
  | { t: 'config'; config: GameConfig }
  | PowerUpMessage
  | { t: 'dailyAttempt'; roomCode: string }
  | {
      t: 'blocked';
      blockedUntil: number;
      blockMessage: string | null;
      blockedFeatures: ProfileBlockFeatures | null;
      /** The server-authoritative room role/mode this attempted action matched. */
      feature: ProfileBlockFeature;
    }
  | { t: 'error'; code: ErrorCode; msg: string }
  | { t: 'kicked'; reason: string };

const WORD_REJECT_REASONS = [
  'late',
  'short',
  'long',
  'dup',
  'notword',
  'notboard',
  'spectator',
  'round',
  'limit',
  'frozen',
] as const;
export type WordRejectReason = (typeof WORD_REJECT_REASONS)[number];
const ERROR_CODES = [
  'invalid',
  'nojoin',
  'full',
  'nothost',
  'auth',
  'profile',
  'busy',
  'origin',
  'rate',
  'notfound',
] as const;
type ErrorCode = (typeof ERROR_CODES)[number];

type ProtocolError = { ok: false; code: 'invalid'; message: string };
type ParsedMessage = { ok: true; value: ClientMessage } | ProtocolError;

const short = (max: number) => z.string().max(max);
const roundIdSchema = short(MAX_ROUND_ID_LENGTH).min(1);
/** Tile indices of the trace behind a word; the largest board has 25 tiles. */
const MAX_PATH_TILES = 25;
const pathSchema = z
  .array(
    z
      .number()
      .int()
      .min(0)
      .max(MAX_PATH_TILES - 1),
  )
  .min(1)
  .max(MAX_PATH_TILES);
/** Unknown keys are stripped so removed fields (for example a word-level token) never reach handlers. */
const clientSchema = z.discriminatedUnion('t', [
  z.object({ t: z.literal('ping'), c: z.number().finite() }),
  z.object({
    t: z.literal('join'),
    // Name and avatar are read from the authenticated profile, never presented by the client.
    token: short(128).optional(),
    profileCredential: short(256).optional(),
  }),
  z.object({
    t: z.literal('word'),
    w: short(64),
    roundId: roundIdSchema,
    submissionId: short(64).optional(),
    /** The traced tiles; only chaos pickups read it, and only after geometry validation. */
    path: pathSchema.optional(),
    /** Only spawns encountered on entry to a traced tile; at most one per tile. */
    pickupIds: z.array(powerUpSpawnSchema.shape.id.min(1)).max(MAX_PATH_TILES).optional(),
  }),
  z.object({ t: z.literal('hint'), roundId: roundIdSchema }),
  z.object({ t: z.literal('finish'), roundId: roundIdSchema }),
  z.object({ t: z.literal('begin'), roundId: roundIdSchema }),
  /** Endless: the player touched this board, which starts its clock. */
  z.object({ t: z.literal('boardActive'), roundId: roundIdSchema, board: z.number().int().min(1) }),
  z.object({ t: z.literal('ready') }),
  z.object({ t: z.literal('setConfig'), config: z.record(z.string(), z.unknown()) }),
  z.object({ t: z.literal('kick'), id: short(80) }),
  z.object({ t: z.literal('chat'), text: z.string().trim().min(1).max(CHAT_MAX_LENGTH) }),
  z.object({ t: z.literal('start'), afterRoundId: roundIdSchema.optional() }),
  z.object({ t: z.literal('cancelStart') }),
  z.object({ t: z.literal('lobby') }),
  z.object({ t: z.literal('resetScores') }),
  z.object({ t: z.literal('leave') }),
]);

function protocolMessage(error: z.ZodError): string {
  const issue = error.issues[0];
  const field = issue?.path[0];
  if (field === 't' || field === undefined) return 'Unknown message type';
  if (issue?.code === 'too_big') return `${String(field)} must be a short string`;
  if (issue?.code === 'too_small') return `${String(field)} is required`;
  return `${String(field)} is invalid`;
}

/** Parse and bound every client-controlled WebSocket message before dispatch. */
export function parseClientMessage(input: unknown): ParsedMessage {
  const parsed = clientSchema.safeParse(input);
  if (parsed.success) return { ok: true, value: parsed.data };
  return { ok: false, code: 'invalid', message: protocolMessage(parsed.error) };
}

const number = z.number();
const phaseSchema = z.enum(PHASES);
/** A 600-second round at one spawn per interval stays well under this. */
const MAX_POWER_UPS_PER_ROUND = 100;
const playerSchema = z.object({
  id: text,
  name: text,
  avatar: text,
  connected: z.boolean(),
  count: number,
  total: number,
  host: z.boolean(),
  spectator: z.boolean().optional(),
  turn: z.enum(TURN_PROGRESS).optional(),
  ready: z.boolean().optional(),
});
const chatMessageSchema = z.object({
  id: text,
  playerId: text,
  name: text,
  avatar: text,
  text: z.string().min(1).max(CHAT_MAX_LENGTH),
  at: number,
  kind: z.enum(['player', 'system']).optional(),
});
const resultsSchema = roundResultsSchema;
const serverSchema = z.discriminatedUnion('t', [
  z.object({ t: z.literal('pong'), c: number, s: number }),
  z.object({ t: z.literal('welcome'), id: text, token: text }),
  z.object({
    t: z.literal('state'),
    now: number,
    phase: phaseSchema,
    round: number,
    currentRoundId: text.nullable(),
    config: configSchema,
    mode: modeSchema,
    dailyDate: dailyDateSchema.optional(),
    activeConfig: configSchema.nullable(),
    startAt: number,
    endAt: number,
    board: boardSchema.nullable(),
    players: z.array(playerSchema).max(MAX_PLAYERS),
    chat: z.array(chatMessageSchema).max(CHAT_HISTORY_LIMIT),
    results: resultsSchema.nullable(),
    revealAt: z.number().nullable().optional(),
    readyCheck: z.object({ endAt: number }).optional(),
    resultLifecycle: z.object({
      continuation: z.enum(['continue-room', 'archive-only']),
      archiveRoundId: text.nullable(),
    }),
    joinUrl: text,
    powerUps: z.array(powerUpSpawnSchema).max(MAX_POWER_UPS_PER_ROUND).optional(),
    endlessBoard: z.object({ number: z.number().int().min(1), endAt: number }).optional(),
    you: z.object({
      id: text,
      name: text,
      avatar: text,
      words: z.array(wordSchema).max(2000),
      spectator: z.boolean(),
      finished: z.boolean().optional(),
      begun: z.boolean().optional(),
      // At most one active effect per timed kind.
      effects: z.array(activeEffectSchema).max(TIMED_EFFECT_KINDS.length).optional(),
      shield: z.boolean().optional(),
      solutionFilter: solutionFilterSchema.optional(),
    }),
  }),
  z.object({ t: z.literal('players'), players: z.array(playerSchema).max(MAX_PLAYERS) }),
  z.object({ t: z.literal('count'), id: text, n: number }),
  z.object({ t: z.literal('chat'), message: chatMessageSchema }),
  z.object({ t: z.literal('hint'), roundId: text, w: z.string().max(64).nullable() }),
  z
    .object({
      t: z.literal('wordResult'),
      submissionId: z.string().max(64).optional(),
      roundId: text,
      w: text,
      ok: z.boolean(),
      pts: number.optional(),
      reason: z.enum(WORD_REJECT_REASONS).optional(),
    })
    .refine((value) => (value.ok ? value.pts !== undefined : value.reason !== undefined)),
  z.object({ t: z.literal('config'), config: configSchema }),
  z.object({
    t: z.literal('powerUp'),
    roundId: text,
    id: text,
    kind: z.enum(POWER_UP_KINDS),
    by: text,
    targets: z.array(text).max(MAX_PLAYERS),
    shielded: z.array(text).max(MAX_PLAYERS),
    until: number.nullable(),
  }),
  z.object({ t: z.literal('dailyAttempt'), roomCode: z.string().regex(ROOM_CODE_RE) }),
  z.object({
    t: z.literal('blocked'),
    blockedUntil: z.number().int().positive(),
    blockMessage: text.nullable(),
    blockedFeatures: profileBlockFeaturesSchema.nullable(),
    feature: profileBlockFeatureSchema,
  }),
  z.object({ t: z.literal('error'), code: z.enum(ERROR_CODES), msg: text }),
  z.object({ t: z.literal('kicked'), reason: text }),
]);

export function isServerMessage(value: unknown): value is ServerMessage {
  return serverSchema.safeParse(value).success;
}
